import { useRef, useState } from 'react';
import { Autocomplete, Box, Button, Chip, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import AttachFile from '@mui/icons-material/AttachFile';
import Search from '@mui/icons-material/Search';
import UploadFile from '@mui/icons-material/UploadFileOutlined';
import { useQuery } from '@tanstack/react-query';
import { formatJordan, ROUTES, type CardFile, type MailAttachment } from '@serb/shared';
import { api } from '../../api/client';
import { fileSize } from '../../lib/text';
import { useAction } from '../../lib/useAction';

type Props = { files: CardFile[]; onChange: (files: CardFile[]) => void; code: string };

/** What goes on the email: the files already on it, and three ways to add one (a received email, your computer, the vault) */
export function AttachmentPicker({ files, onChange, code }: Props) {
  const add = (f: CardFile) => onChange(files.some((x) => x.path === f.path) ? files : [...files, f]);
  const input = useRef<HTMLInputElement>(null);
  const [vaultSearch, setVaultSearch] = useState('');
  const [typed, setTyped] = useState(code);
  const [mailSearch, setMailSearch] = useState(code);

  const vault = useQuery({
    queryKey: ['attachments', vaultSearch],
    queryFn: () => api.get<CardFile[]>(ROUTES.api.attachments, { q: vaultSearch }),
  });
  const mail = useQuery({
    queryKey: ['mail-attachments', mailSearch],
    queryFn: () => api.get<MailAttachment[]>(ROUTES.api.mailAttachments, { q: mailSearch }),
  });
  const upload = useAction(
    (file: File) => api.upload<CardFile>(ROUTES.api.attachmentUpload, file, { name: file.name }),
    {
      success: (f) => `${f?.name} added.`,
      onDone: add,
    },
  );
  const fromMail = useAction((m: MailAttachment) => api.post<CardFile>(ROUTES.api.mailAttachmentSave, m), {
    success: (f) => `${f?.name} added.`,
    onDone: add,
  });

  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Attachments
      </Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {files.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No files attached.
          </Typography>
        )}
        {files.map((f) => (
          <Chip
            key={f.path}
            label={`${f.name} · ${fileSize(f.size)}`}
            color={f.size === null ? 'error' : 'default'}
            onDelete={() => onChange(files.filter((x) => x.path !== f.path))}
          />
        ))}
      </Stack>

      <Stack spacing={1.5}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            From an email you received (type the SRB code or a word from the email, then press Enter)
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), setMailSearch(typed))}
            slotProps={{
              htmlInput: { 'aria-label': 'Find a file in received emails' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          {mail.isFetching && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Looking in your mailbox…
            </Typography>
          )}
          {mail.isError && (
            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
              Could not look in the mailbox right now.
            </Typography>
          )}
          {mail.data?.length === 0 && !mail.isFetching && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              No files found on emails matching “{mailSearch}”.
            </Typography>
          )}
          <Stack spacing={0.5} sx={{ mt: 0.5, maxHeight: 220, overflowY: 'auto' }}>
            {mail.data?.map((m) => (
              <Stack key={`${m.mailbox}-${m.uid}-${m.part}`} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {m.name} · {fileSize(m.size)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    {m.from} · {m.date ? formatJordan(m.date) : ''} · {m.subject}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={fromMail.busy || files.some((f) => f.name === m.name)}
                  onClick={() => fromMail.run(m)}
                >
                  Add
                </Button>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
          <input
            ref={input}
            type="file"
            hidden
            aria-label="Choose a file from your computer"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.run(file);
              e.target.value = '';
            }}
          />
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            disabled={upload.busy}
            onClick={() => input.current?.click()}
          >
            Upload From My Computer
          </Button>
          <Autocomplete
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            options={(vault.data ?? []).filter((f) => !files.some((x) => x.path === f.path))}
            getOptionLabel={(f) => f.path}
            filterOptions={(x) => x}
            value={null}
            inputValue={vaultSearch}
            onInputChange={(_e, v, reason) => reason === 'input' && setVaultSearch(v)}
            onChange={(_e, f) => {
              if (f) add(f);
              setVaultSearch('');
            }}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Or a file in the vault"
                placeholder="Type part of its name"
                slotProps={{
                  ...p.slotProps,
                  input: {
                    ...p.slotProps.input,
                    startAdornment: <AttachFile fontSize="small" sx={{ mr: 0.5 }} />,
                  },
                }}
              />
            )}
          />
        </Stack>
      </Stack>
    </Box>
  );
}
